import { apiFetch, withApiRequestScope } from "../lib/api";
import { HierarchyAccessError, id, object } from "./api";
import { boundedBytes } from "./response";

export interface HistoricalMessage {
  id: string;
  agentId: string;
  ordinal: number;
  role: "user" | "assistant";
  kind: string;
  text: string;
  createdAt: string;
  original: Record<string, unknown>;
}

export interface HistoricalTurn {
  messages: HistoricalMessage[];
  // Exact server JSON retains null and large integer evidence without a JS
  // Number round-trip. Parsed fields below are for presentation, never billing
  // or execution. This data is memory-only and has the transcript's lifetime.
  evidenceJSON: string;
}

export async function requestTurn(path: string, signal?: AbortSignal): Promise<{ body: Record<string, unknown>; evidenceJSON: string }> {
  return withApiRequestScope(async (active) => {
    const response = await apiFetch(path, { credentials: "include", cache: "no-store", signal: active });
    const bytes = await boundedBytes(response, 64 << 20, active);
    const evidenceJSON = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return { body: object(JSON.parse(evidenceJSON)), evidenceJSON };
  }, signal);
}

export function historicalEvidence(raw: Record<string, unknown>, evidenceJSON: string): HistoricalTurn | undefined {
  if (raw.legacy === undefined) return undefined;
  const legacy = object(raw.legacy), scope = object(raw.scope), input = object(raw.input), result = object(raw.result);
  if (legacy.version !== 1 || scope.run_id !== "" || raw.stateless === true || (raw.snapshot_id !== undefined && raw.snapshot_id !== "") ||
    [legacy.projection_hash, legacy.snapshot_hash].some((v) => typeof v !== "string" || !/^[a-fA-F0-9]{64}$/.test(v)) ||
    [result.error_code, result.error_summary].some((v) => v !== undefined && v !== "") ||
    result.usage !== null || [input.artifacts, result.artifacts].some((v) => v != null && (!Array.isArray(v) || v.length !== 0)) ||
    !Array.isArray(legacy.messages) || !legacy.messages.length || legacy.messages.length > 100_000) throw new HierarchyAccessError();
  const messages = legacy.messages.map((value): HistoricalMessage => {
    const row = object(value);
    if (row.agentId !== scope.thread_id || (row.role !== "user" && row.role !== "assistant") ||
      !Number.isSafeInteger(row.ordinal) || Number(row.ordinal) < 1 || typeof row.text !== "string" || row.text.includes("\0") ||
      typeof row.createdAt !== "string" || !Number.isFinite(Date.parse(row.createdAt))) throw new HierarchyAccessError();
    return { id: id(row.id), agentId: id(row.agentId), ordinal: Number(row.ordinal), role: row.role,
      kind: id(row.kind), text: row.text, createdAt: row.createdAt, original: row };
  });
  if (new Set(messages.map((m) => m.id)).size !== messages.length || messages.some((m, i) => i > 0 &&
    (m.ordinal !== messages[i - 1].ordinal + 1 || Date.parse(m.createdAt) < Date.parse(messages[i - 1].createdAt)))) throw new HierarchyAccessError();
  const originalInput = messages.find((m) => m.id === legacy.input_message_id), originalResult = messages.find((m) => m.id === legacy.result_message_id);
  if (!originalInput || !originalResult || originalInput.ordinal >= originalResult.ordinal || originalInput.role !== "user" || originalResult.role !== "assistant" ||
    originalInput.text !== input.text || originalResult.text !== result.reply) throw new HierarchyAccessError();
  return { messages, evidenceJSON };
}
